import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Step,
  StepLabel,
  Stepper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { StyledTextField } from "../../Components/TextField/TextField";
import {
  ClientType,
  ClientTypeLookup,
  ClientTypeReverseLookup,
} from "../../Enums/ClientType";
import { ProductType } from "../../Enums/ProductType";
import { useHereDotComAccountsQuery } from "../../Queries/ClientManagementQueries";
import { getClientsByProduct } from "../../Services/API/BaseClientManagement";
import { createClient } from "../../Services/API/ClientManagement";
import { BaseClient } from "../../Types/BaseClientManagement";
import { CreateClient, HereDotcomAccount } from "../../Types/ClientManagement";
import AutoCompleteComponent from "../AutoComplete/AutoComplete";
import DropdownComponent from "../Dropdown/DropdownComponent";
import LoadingBar from "../LoadingBar/LoadingBar";

interface CreateClientModalProps {
  openModal: Dispatch<SetStateAction<boolean>>;
  setShowSignifier: Dispatch<SetStateAction<boolean>>;
  setShowError: Dispatch<SetStateAction<boolean>>;
  createProcessing: boolean;
  setCreateProcessing: Dispatch<SetStateAction<boolean>>;
}

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    justifyContent: "space-between",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  sensitiveButton: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      width: "100%",
    },
    marginTop: "20px",
    width: "200px",
  },
  textField: {
    "& .Mui-error": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiFormHelperText-root": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiInput-underline.Mui-error:after": {
      borderBottomColor: "rgba(255,0,0,.7) !important",
    },
  },
}));

const steps = ["Select Base Client", "Create Careers Client"];

const sortBaseClients = (a: BaseClient, b: BaseClient) => {
  const aName = a.clientName.toLowerCase();
  const bName = b.clientName.toLowerCase();
  if (aName === bName) {
    return 0;
  }
  return aName < bName ? -1 : 1;
};

export default function CreateClientModal({
  openModal,
  setShowSignifier,
  setShowError,
  createProcessing,
  setCreateProcessing,
}: CreateClientModalProps) {
  const classes = useStyles();
  const queryClient = useQueryClient();

  const [baseClients, setBaseClients] = useState<BaseClient[]>([]);
  const [selectedBaseClient, setSelectedBaseClient] = useState<BaseClient | null>(null);
  const [clientName, setClientName] = useState("");
  const [hostName, setHostName] = useState("");
  const [clientType, setClientType] = useState(ClientTypeReverseLookup[ClientType.Active]);
  const [shortName, setShortName] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [themeName, setThemeName] = useState("");
  const [talentegyTrackingId, setTalentegyTrackingId] = useState("");
  const [talentNetworkEmbedScript, setTalentNetworkEmbedScript] = useState("");

  const {
    data: { hereDotComBetaValues = [], hereDotComProdValues = [] } = {},
  } = useHereDotComAccountsQuery();
  const [hereDotComBetaAccount, setHereDotComBetaAccount] = useState<HereDotcomAccount>(
    {} as HereDotcomAccount
  );
  const [hereDotComProdAccount, setHereDotComProdAccount] = useState<HereDotcomAccount>(
    {} as HereDotcomAccount
  );

  const [ssl, setSsl] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [advancedCookies, setAdvancedCookies] = useState(false);
  const [proxySort, setProxySort] = useState(false);
  const [latestJobs, setLatestJobs] = useState(false);
  const [brandAsOrg, setBrandAsOrg] = useState(false);
  const [isNotActivate, setIsNotActivate] = useState(false);

  const [showInputError, setShowInputError] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  function saveClientJson() {
    if (
      clientName === "" ||
      shortName === "" ||
      hostName === "" ||
      companyWebsite === "" ||
      talentNetworkEmbedScript.length > 1000
    ) {
      setShowInputError(true);
      return;
    }

    let json = {
      name: clientName,
      shortName,
      hostname: hostName,
      type: ClientTypeLookup[clientType],
      companyWebsite,
      themeName,
      talentegyTrackingId: talentegyTrackingId,
      talentNetworkEmbedScript,
      isSsl: ssl,
      isGdpr: gdpr,
      isAdvancedCookie: advancedCookies,
      hasProxySort: proxySort,
      sendLatestJobsTalentNetwork: latestJobs,
      useBrandAsOrg: brandAsOrg,
      hereDotComBetaAccountId: hereDotComBetaAccount.id,
      hereDotComProductionAccountId: hereDotComProdAccount.id,
      isNotActivateClient: isNotActivate,
      centralClientID: selectedBaseClient?.id,
    } as CreateClient;

    setCreateProcessing(true);
    setShowInputError(false);
    createClient(json).then((success) => {
      if (success) {
        queryClient.refetchQueries(["allCareersClients"]).then(() => {
          setShowSignifier(true);
          setCreateProcessing(false);
          openModal(false);
        });
      } else {
        setShowSignifier(true);
        setCreateProcessing(true);
        setShowError(true);
      }
    });
  }

  useEffect(() => {
    getClientsByProduct(ProductType.Careers).then((response) => {
      response.sort(sortBaseClients);
      setBaseClients(response);
    });
  }, []);

  function setHereDotComId(id: string, isBeta: boolean) {
    if (isBeta) {
      if (!id) {
        setHereDotComBetaAccount(hereDotComBetaValues[0]);
      } else {
        setHereDotComBetaAccount(
          hereDotComBetaValues.find((account) => account.id === id) ?? hereDotComBetaAccount
        );
      }
    } else {
      if (!id) {
        setHereDotComProdAccount(hereDotComProdValues[0]);
      } else {
        setHereDotComProdAccount(
          hereDotComProdValues.find((account) => account.id === id) ?? hereDotComProdAccount
        );
      }
    }
  }

  function handleNext() {
    if (activeStep === 0) {
      if (selectedBaseClient !== null) {
        setActiveStep(activeStep + 1);
        setShowInputError(false);
      } else {
        setShowInputError(true);
      }
    }
  }

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};

          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Grid>
        <LoadingBar loading={createProcessing} />
        <Card>
          {activeStep === 0 ? (
            <CardContent>
              <Grid container spacing={1}>
                <Grid xs={12} sm={12} item>
                  <AutoCompleteComponent
                    id="createclient-base-client-dropdown"
                    dropdownItems={baseClients}
                    propertyName="clientName"
                    label="Base Clients"
                    onChange={(_, client) => setSelectedBaseClient(client)}
                    currValue={selectedBaseClient}
                  />
                  {showInputError && selectedBaseClient === null ? (
                    <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                      Base Client Required
                    </FormHelperText>
                  ) : null}
                </Grid>
                <Grid xs={12} sm={6} item>
                  <Button
                    style={{ marginTop: "30px", width: "95%" }}
                    type="submit"
                    variant="outlined"
                    color="primary"
                    onClick={() => openModal(false)}
                    disabled={createProcessing}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid xs={12} sm={6} item>
                  <Button
                    style={{ marginTop: "30px", width: "95%" }}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={() => handleNext()}
                    disabled={createProcessing}
                    data-test={"createclient-select-base-next"}
                  >
                    Next
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          ) : (
            <CardContent>
              <Grid container spacing={1}>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="Client Name"
                    label="Client Name"
                    variant="standard"
                    fullWidth
                    required
                    error={showInputError && clientName === ""}
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    data-test={"createclient-name-input"}
                  />
                  {showInputError && clientName === "" ? (
                    <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                      Client Name Required
                    </FormHelperText>
                  ) : null}
                </Grid>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="Short Name"
                    label="Short Name"
                    variant="standard"
                    fullWidth
                    required
                    error={showInputError && shortName === ""}
                    value={shortName}
                    onChange={(e) => setShortName(e.target.value)}
                    data-test={"createclient-shortname-input"}
                  />
                  {showInputError && shortName === "" ? (
                    <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>Short Name Required</FormHelperText>
                  ) : null}
                </Grid>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="Host Name"
                    label="Host Name"
                    variant="standard"
                    fullWidth
                    required
                    error={showInputError && hostName === ""}
                    value={hostName}
                    onChange={(e) => setHostName(e.target.value)}
                    data-test={"createclient-hostname-input"}
                  />
                  {showInputError && hostName === "" ? (
                    <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>Host Name Required</FormHelperText>
                  ) : null}
                </Grid>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="Company Website"
                    label="Company Website"
                    variant="standard"
                    fullWidth
                    required
                    error={showInputError && companyWebsite === ""}
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    data-test={"createclient-company-website-input"}
                  />
                  {showInputError && companyWebsite === "" ? (
                    <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                      Company Website Required
                    </FormHelperText>
                  ) : null}
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="Theme Name"
                    label="Theme Name"
                    variant="standard"
                    fullWidth
                    value={themeName}
                    onChange={(e) => setThemeName(e.target.value)}
                    data-test={"createclient-theme-name-input"}
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="Talentegy Tracking Code"
                    label="Talentegy Tracking Code"
                    variant="standard"
                    fullWidth
                    value={talentegyTrackingId}
                    onChange={(e) => setTalentegyTrackingId(e.target.value)}
                    data-test={"createclient-talentegy-tracking-input"}
                  />
                </Grid>
                <Grid xs={12} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    placeholder="Embed Script"
                    label="CRM Talent Network Embed Script"
                    value={talentNetworkEmbedScript}
                    fullWidth
                    variant="standard"
                    onChange={(e) => setTalentNetworkEmbedScript(e.target.value)}
                    data-test="edit-base-client-tnembed-script"
                  />
                  {showInputError && talentNetworkEmbedScript.length > 1000 && (
                    <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                      Embed script cannot be longer than 1000 characters
                    </FormHelperText>
                  )}
                </Grid>
                <Grid xs={12} sm={4} item>
                  <DropdownComponent
                    currValue={clientType}
                    items={[
                      ClientTypeReverseLookup[ClientType.Active],
                      ClientTypeReverseLookup[ClientType.Inactive],
                      ClientTypeReverseLookup[ClientType.Test],
                    ]}
                    handleChange={(item: string) => setClientType(item)}
                    title="Client Type"
                    error={false}
                    itemIdSelector={undefined}
                    multi={undefined}
                    property={undefined}
                  />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <DropdownComponent
                    currValue={hereDotComBetaAccount.id ?? ""}
                    items={hereDotComBetaValues}
                    handleChange={(id: string) => setHereDotComId(id, true)}
                    property="name"
                    title="Here.com Beta Account"
                    data-test={"createclient-here-betaaccount-dropdown"}
                    multi={false}
                    error={false}
                    itemIdSelector={(item: HereDotcomAccount) => item.id}
                  />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <DropdownComponent
                    currValue={hereDotComProdAccount.id ?? ""}
                    items={hereDotComProdValues}
                    handleChange={(id: string) => setHereDotComId(id, false)}
                    property="name"
                    title="Here.com Production Account"
                    data-test={"createclient-here-prodaccount-dropdown"}
                    multi={false}
                    error={false}
                    itemIdSelector={(item: HereDotcomAccount) => item.id}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1} style={{ marginTop: "20px" }}>
                <Grid xs={12} sm={4} item>
                  <FormControlLabel
                    control={
                      <Checkbox checked={ssl} color="primary" onChange={(e) => setSsl(e.target.checked)} />
                    }
                    label="SSL"
                    data-test={"createclient-ssl-checkbox"}
                  />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <FormControlLabel
                    control={
                      <Checkbox checked={gdpr} color="primary" onChange={(e) => setGdpr(e.target.checked)} />
                    }
                    label="GDPR"
                    data-test={"createclient-gdpr-checkbox"}
                  />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={advancedCookies}
                        color="primary"
                        onChange={(e) => setAdvancedCookies(e.target.checked)}
                      />
                    }
                    label="Advanced Cookies"
                    data-test={"createclient-advancedcookies-checkbox"}
                  />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={proxySort}
                        color="primary"
                        onChange={(e) => setProxySort(e.target.checked)}
                      />
                    }
                    label="Results Proximity Sort"
                    data-test={"createclient-resultssort-checkbox"}
                  />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={latestJobs}
                        color="primary"
                        onChange={(e) => setLatestJobs(e.target.checked)}
                      />
                    }
                    label="Only Send Latest Job Alerts"
                    data-test={"createclient-sendlatestjobalerts-checkbox"}
                  />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={brandAsOrg}
                        color="primary"
                        onChange={(e) => setBrandAsOrg(e.target.checked)}
                      />
                    }
                    label="Use Brand as Organization Name (Google Jobs object)"
                    data-test={"createclient-usebrandorgname-checkbox"}
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isNotActivate}
                        color="primary"
                        onChange={(e) => setIsNotActivate(e.target.checked)}
                      />
                    }
                    label="Not An Activate Client"
                    data-test={"createclient-notactivate-checkbox"}
                  />
                </Grid>
              </Grid>

              <Grid xs={12} sm={12} item container style={{ justifyContent: "flex-end" }}>
                <Grid xs={12} sm={3} item>
                  <Button
                    style={{ marginTop: "30px", width: "95%" }}
                    type="submit"
                    variant="outlined"
                    color="primary"
                    onClick={() => setActiveStep(activeStep - 1)}
                    disabled={createProcessing}
                  >
                    Back
                  </Button>
                </Grid>
                <Grid xs={12} sm={3} item>
                  <Button
                    className={classes.sensitiveButton}
                    style={{ marginTop: "30px", width: "95%" }}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={saveClientJson}
                    disabled={createProcessing}
                    data-test={"createclient-create-button"}
                  >
                    Create Client
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          )}
        </Card>
      </Grid>
    </div>
  );
}
